import React, { useEffect, useRef, useState } from 'react';

const STEPS = [
  {
    num: '01',
    title: 'Connect your ad accounts',
    desc: 'Link TikTok, Meta, LinkedIn, Snapchat and Google in seconds via secure OAuth. All tokens stay encrypted on our servers.',
    color: '#221C51',
    bg: 'rgba(34,28,81,0.07)',
    border: 'rgba(34,28,81,0.15)',
  },
  {
    num: '02',
    title: 'Define your campaign',
    desc: 'Choose your objective, set your budget, and target your audience. Our AI pre-fills recommendations based on your niche.',
    color: '#1877f2',
    bg: 'rgba(24,119,242,0.06)',
    border: 'rgba(24,119,242,0.15)',
  },
  {
    num: '03',
    title: 'Launch across all platforms',
    desc: 'Hit Launch once — your campaign goes live on every connected platform simultaneously, with platform-specific formatting.',
    color: '#059669',
    bg: 'rgba(5,150,105,0.06)',
    border: 'rgba(5,150,105,0.15)',
  },
  {
    num: '04',
    title: 'Monitor & automate',
    desc: 'Watch live metrics in the Campaign Manager. Set rules to auto-pause underperformers and reallocate budget.',
    color: '#D97706',
    bg: 'rgba(217,119,6,0.06)',
    border: 'rgba(217,119,6,0.15)',
  },
];

const STATS = [
  { label: 'Time saved per campaign', value: '4 hrs', color: '#221C51', bg: '#F5F7FF', border: '#E0E4F0' },
  { label: 'Platforms supported', value: '6', color: '#1877f2', bg: '#EEF4FF', border: '#BFDBFE' },
  { label: 'Campaigns launched', value: '5K+', color: '#059669', bg: '#ECFDF5', border: '#A7F3D0' },
  { label: 'Happy clients', value: '200+', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
];

const About = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={ref} style={{ background: '#F6F7FB', padding: '110px 24px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* About us */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: 64, alignItems: 'center', marginBottom: 96 }}>
          <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateX(-30px)', transition: 'all 0.7s ease' }}>
            <div className="mono-label" style={{ color: '#737373', marginBottom: 24 }}>
              <span style={{ color: '#4F46E5' }}>/</span> About Us
            </div>
            <h2 style={{ fontSize: 'clamp(28px,4vw,44px)', fontWeight: 700, color: '#111', letterSpacing: '-0.035em', marginBottom: 20, lineHeight: 1.1 }}>
              Built by media buyers,{' '}
              <em className="serif-accent">for media buyers.</em>
            </h2>
            <p style={{ fontSize: 15, color: '#737373', lineHeight: 1.75, marginBottom: 16 }}>
              Welcome to <strong style={{ color: '#111' }}>ONEBOTADS</strong> — the unified command centre for your digital advertising. We built this platform because we were tired of jumping between TikTok Ads Manager, Meta Business Suite, LinkedIn Campaign Manager and three other tabs just to run one campaign.
            </p>
            <p style={{ fontSize: 15, color: '#737373', lineHeight: 1.75 }}>
              Our mission: reduce the time from <em style={{ color: '#404040' }}>idea</em> to <em style={{ color: '#404040' }}>live ad</em> from hours to minutes.
            </p>
          </div>

          {/* Stat cards */}
          <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateX(30px)', transition: 'all 0.7s 0.15s ease', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {STATS.map((item, i) => (
              <div key={i} style={{
                padding: '24px 20px', borderRadius: 14,
                background: '#fff',
                border: '1px solid #E5E6EF',
              }}>
                <div style={{ fontSize: 30, fontWeight: 700, color: '#111', marginBottom: 6, fontFamily: "'Space Grotesk',sans-serif", letterSpacing: '-0.02em' }}>{item.value}</div>
                <div style={{ fontSize: 12, color: '#737373', lineHeight: 1.4 }}>{item.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div id="how-it-works">
          <div style={{ marginBottom: 56 }}>
            <div className="mono-label" style={{ color: '#737373', marginBottom: 20 }}>
              <span style={{ color: '#4F46E5' }}>/</span> How it works
            </div>
            <h2 style={{ fontSize: 'clamp(26px,4vw,42px)', fontWeight: 700, color: '#111', letterSpacing: '-0.035em', lineHeight: 1.1 }}>
              From idea to live ad in{' '}
              <em className="serif-accent">4 simple steps.</em>
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: 20 }}>
            {STEPS.map((s, i) => (
              <div key={i} style={{
                padding: '30px 26px', borderRadius: 14,
                background: '#fff',
                border: '1px solid #E5E6EF',
                opacity: visible ? 1 : 0,
                transform: visible ? 'none' : 'translateY(24px)',
                transition: `all 0.6s ${i * 100 + 200}ms ease`,
              }}>
                <div className="mono-label" style={{ color: '#4F46E5', marginBottom: 16 }}>{s.num}</div>
                <div style={{ width: 28, height: 2, background: '#111', marginBottom: 20 }} />
                <h4 style={{ fontSize: 15.5, fontWeight: 700, color: '#111', marginBottom: 10 }}>{s.title}</h4>
                <p style={{ fontSize: 13.5, color: '#737373', lineHeight: 1.65 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default About;
